import React, { createContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type Language = 'en' | 'ru' | 'hy';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, options?: any) => string;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { i18n, t: i18nT } = useTranslation();
  
  const [language, setLanguageState] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('elitmed-language') as Language;
    if (savedLanguage && ['en', 'ru', 'hy'].includes(savedLanguage)) {
      return savedLanguage;
    }
    
    const browserLang = navigator.language.split('-')[0];
    if (['en', 'ru', 'hy'].includes(browserLang)) {
      return browserLang as Language;
    }
    
    return 'hy';
  });

  useEffect(() => {
    localStorage.setItem('elitmed-language', language);
    i18n.changeLanguage(language);
    document.documentElement.lang = language;
  }, [language, i18n]);

  const setLanguage = (newLanguage: Language) => {
    setLanguageState(newLanguage);
  };

  const t = (key: string, options?: any): string => {
    const result = i18nT(key, options);
    return typeof result === 'string' ? result : String(result);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};